import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Confirm, Label, Loader, Message, Table } from 'semantic-ui-react'
import { FlexRowBetween } from 'components'
import {
    GetNotificationGroupCustomerList,
    GetTempleteList,
    GetUserListForGroup,
} from 'hooks'
import { useNotification } from 'hooks/toast'
import { AuthService, MaintenanceService, PaginationType, canStartAuditsByRole } from 'utility'
import { MaintenanceTaskModal } from './MaintenanceTaskModal'
import { MaintenanceTaskDetailModal } from './MaintenanceTaskDetailModal'
import { MaintenanceScheduleModal } from './MaintenanceScheduleModal'
import {
    PRIORITY_COLORS,
    canManageMaintenanceSchedules,
    formatDate,
    maintenanceApiError,
} from './maintenance-utils'

const SectionHeader = ({ title, children }) => (
    <FlexRowBetween className="mb-2 mt-3">
        <div style={{ fontWeight: 600, fontSize: 16 }}>{title}</div>
        <div>{children}</div>
    </FlexRowBetween>
)

/**
 * Asset Details > Maintenance: the asset's schedules (EAUDITOR-405 / 406) and its maintenance tasks (EAUDITOR-407).
 */
export const AssetMaintenanceTab = ({ asset, compact = false }) => {
    const assetId = asset?.AssetId
    const user = AuthService.getUser()
    const companyId = user?.Companies?.[0]?.CompanyId || user?.CompanyId
    const canCreate = canStartAuditsByRole(user)
    const canManageSchedules = canManageMaintenanceSchedules(user)
    const { successToast, errorToast } = useNotification()
    const [rows, setRows] = useState([])
    const [schedules, setSchedules] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [busy, setBusy] = useState(false)
    const [createOpen, setCreateOpen] = useState(false)
    const [detailTaskId, setDetailTaskId] = useState(null)
    const [scheduleModal, setScheduleModal] = useState(null) // { id } (null id = new)
    const [deleteSchedule, setDeleteSchedule] = useState(null)

    const { options: templateOptions } = GetTempleteList(PaginationType.All)
    const { options: userOptions } = GetUserListForGroup(PaginationType.All)
    const { options: customerOptions } = GetNotificationGroupCustomerList(companyId)

    // answers for an asset the user already left are dropped
    const currentAsset = useRef(assetId)
    currentAsset.current = assetId

    const load = useCallback(async () => {
        if (!assetId) return
        setLoading(true)
        setError('')
        // each list on its own: a failing schedules call must not hide the tasks (and the other way round)
        const [tasks, scheds] = await Promise.allSettled([
            MaintenanceService.byAsset(assetId),
            MaintenanceService.schedulesByAsset(assetId),
        ])
        if (currentAsset.current !== assetId) return
        const errors = []
        if (tasks.status === 'fulfilled') setRows(Array.isArray(tasks.value) ? tasks.value : [])
        else {
            setRows([])
            errors.push(maintenanceApiError(tasks.reason, 'Maintenance tasks could not be loaded.'))
        }
        if (scheds.status === 'fulfilled') setSchedules(Array.isArray(scheds.value) ? scheds.value : [])
        else {
            setSchedules([])
            errors.push(maintenanceApiError(scheds.reason, 'Schedules could not be loaded.'))
        }
        setError(errors.join(' '))
        setLoading(false)
    }, [assetId])

    useEffect(() => {
        load()
    }, [load])

    const act = async (fn, okMessage) => {
        setBusy(true)
        try {
            const result = await fn()
            if (okMessage) successToast(typeof okMessage === 'function' ? okMessage(result) : okMessage)
            await load()
        } catch (e) {
            if (!e?.roleRefusalHandled) errorToast(maintenanceApiError(e))
        } finally {
            setBusy(false)
        }
    }

    const runNow = s =>
        act(
            () => MaintenanceService.runScheduleNow(s.ScheduleId),
            result => {
                if (result?.Generated > 0) return `${result.Generated} task(s) created`
                return (result?.Messages || []).join(' ') || 'Nothing to create'
            },
        )

    return (
        <div>
            {loading && <Loader active inline="centered" />}
            {error && <Message negative content={error} />}

            <SectionHeader title="Schedules">
                {canManageSchedules && (
                    <Button size="small" type="button" onClick={() => setScheduleModal({ id: null })}>
                        New schedule
                    </Button>
                )}
            </SectionHeader>
            {!loading && !error && schedules.length === 0 && (
                <Message info content="No maintenance schedules for this asset yet." />
            )}
            {schedules.length > 0 && (
                <Table compact celled unstackable>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Schedule</Table.HeaderCell>
                            <Table.HeaderCell>Frequency</Table.HeaderCell>
                            {!compact && <Table.HeaderCell>Last completed</Table.HeaderCell>}
                            <Table.HeaderCell>Next due</Table.HeaderCell>
                            {!compact && <Table.HeaderCell>Assigned to</Table.HeaderCell>}
                            <Table.HeaderCell>Status</Table.HeaderCell>
                            {canManageSchedules && <Table.HeaderCell />}
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {schedules.map(s => (
                            <Table.Row key={s.ScheduleId}>
                                <Table.Cell>
                                    <button
                                        type="button"
                                        className="btn btn-link p-0"
                                        style={{ fontSize: 14, textAlign: 'left' }}
                                        onClick={() => setScheduleModal({ id: s.ScheduleId })}
                                    >
                                        {s.Name}
                                    </button>
                                    <div style={{ color: '#6b7280', fontSize: 12 }}>
                                        {[s.MaintenanceTypeName, s.WorkTemplateName].filter(Boolean).join(' · ')}
                                    </div>
                                </Table.Cell>
                                <Table.Cell>{s.FrequencyText}</Table.Cell>
                                {!compact && <Table.Cell>{formatDate(s.LastCompletedOn)}</Table.Cell>}
                                <Table.Cell>
                                    {formatDate(s.NextDueDate)}
                                    {s.LeadDays > 0 && (
                                        <div style={{ color: '#6b7280', fontSize: 12 }}>
                                            task on {formatDate(s.NextGenerationDate)}
                                        </div>
                                    )}
                                </Table.Cell>
                                {!compact && <Table.Cell>{s.AssigneeName || '-'}</Table.Cell>}
                                <Table.Cell>
                                    <Label size="mini" color={s.IsActive ? 'green' : 'grey'}>
                                        {s.Status}
                                    </Label>
                                    {s.FailedOccurrences > 0 && (
                                        <Label
                                            size="mini"
                                            color="red"
                                            className="pointer"
                                            title="Open the schedule to see and retry the failed runs"
                                            onClick={() => setScheduleModal({ id: s.ScheduleId })}
                                        >
                                            {s.FailedOccurrences} failed
                                        </Label>
                                    )}
                                </Table.Cell>
                                {canManageSchedules && (
                                    <Table.Cell collapsing>
                                        {s.IsActive ? (
                                            <>
                                                <Button size="mini" basic disabled={busy} onClick={() => runNow(s)}>
                                                    Run now
                                                </Button>
                                                <Button
                                                    size="mini"
                                                    basic
                                                    disabled={busy}
                                                    onClick={() =>
                                                        act(() => MaintenanceService.pauseSchedule(s.ScheduleId), 'Schedule paused')
                                                    }
                                                >
                                                    Pause
                                                </Button>
                                            </>
                                        ) : (
                                            <Button
                                                size="mini"
                                                basic
                                                disabled={busy}
                                                onClick={() =>
                                                    act(() => MaintenanceService.resumeSchedule(s.ScheduleId), 'Schedule resumed')
                                                }
                                            >
                                                Resume
                                            </Button>
                                        )}
                                        <Button size="mini" basic color="red" disabled={busy} onClick={() => setDeleteSchedule(s)}>
                                            Delete
                                        </Button>
                                    </Table.Cell>
                                )}
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            )}

            <SectionHeader title="Maintenance tasks">
                {canCreate && (
                    <Button primary size="small" type="button" onClick={() => setCreateOpen(true)}>
                        New maintenance task
                    </Button>
                )}
            </SectionHeader>
            {!loading && !error && rows.length === 0 && (
                <Message info content="No maintenance tasks for this asset yet." />
            )}
            {rows.length > 0 && (
                <Table compact celled selectable unstackable>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Task</Table.HeaderCell>
                            {!compact && <Table.HeaderCell>Type</Table.HeaderCell>}
                            <Table.HeaderCell>Priority</Table.HeaderCell>
                            <Table.HeaderCell>Status</Table.HeaderCell>
                            <Table.HeaderCell>Due</Table.HeaderCell>
                            {!compact && <Table.HeaderCell>Assigned to</Table.HeaderCell>}
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {rows.map(row => (
                            <Table.Row
                                key={row.TaskId}
                                className="pointer"
                                onClick={() => setDetailTaskId(row.TaskId)}
                            >
                                <Table.Cell>
                                    {row.Title}
                                    <div style={{ color: '#6b7280', fontSize: 12 }}>{row.StageName}</div>
                                </Table.Cell>
                                {!compact && <Table.Cell>{row.MaintenanceTypeName || '-'}</Table.Cell>}
                                <Table.Cell style={{ color: PRIORITY_COLORS[row.PriorityId] }}>
                                    {row.PriorityName}
                                </Table.Cell>
                                <Table.Cell>
                                    <Label size="mini">{row.TaskStatusName || '-'}</Label>
                                </Table.Cell>
                                <Table.Cell>{formatDate(row.TaskDue)}</Table.Cell>
                                {!compact && <Table.Cell>{row.AssigneeName || '-'}</Table.Cell>}
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            )}
            <MaintenanceTaskModal
                open={createOpen}
                presetAsset={asset}
                templateOptions={templateOptions}
                userOptions={userOptions}
                customerOptions={customerOptions}
                onClose={created => {
                    setCreateOpen(false)
                    if (created) load()
                }}
            />
            <MaintenanceTaskDetailModal
                open={!!detailTaskId}
                taskId={detailTaskId}
                templateOptions={templateOptions}
                userOptions={userOptions}
                onClose={changed => {
                    setDetailTaskId(null)
                    if (changed) load()
                }}
            />
            <MaintenanceScheduleModal
                open={!!scheduleModal}
                assetId={assetId}
                scheduleId={scheduleModal?.id || null}
                canEdit={canManageSchedules}
                templateOptions={templateOptions}
                userOptions={userOptions}
                customerOptions={customerOptions}
                onClose={changed => {
                    setScheduleModal(null)
                    if (changed) load()
                }}
            />
            <Confirm
                open={!!deleteSchedule}
                header="Delete schedule"
                content={`Delete "${deleteSchedule?.Name || ''}"? No new tasks will be created. Tasks already created stay as they are.`}
                confirmButton="Delete"
                onCancel={() => setDeleteSchedule(null)}
                onConfirm={() => {
                    const s = deleteSchedule
                    setDeleteSchedule(null)
                    act(() => MaintenanceService.removeSchedule(s.ScheduleId), 'Schedule deleted')
                }}
            />
        </div>
    )
}
